import {observer} from "mobx-react";
import {useContext} from "react";
import {Typography} from "@mui/material";
import {useTheme} from "@mui/material/styles";
import {Frame} from "../common/Frame";
import {LessonStoreContext} from "../../stores/LessonStore";
import {VideoExampleCard} from "./VideoExampleCard";
import {lessons} from "../../proto/compiled";
import "react-quill-new/dist/quill.snow.css";
import {D} from "../../Constants";
import {SanitizedHtml} from "../common/SanitizedHtml";
import {textBlockStyleColor, textBlockStyleProps} from "./TextBlockStyles";

export const LessonChapterContent = observer(() => {
  const store = useContext(LessonStoreContext);

  if (!store || !store.lesson) return null;

  // Render all chapters sequentially (no sidebar navigation)
  return (
    <Frame direction="column" gap={3}>
      {store.lesson.chapters?.map((chapter) => (
        <ChapterView key={chapter.id} chapter={chapter}/>
      ))}
    </Frame>
  );
});

interface ChapterViewProps {
  chapter: lessons.ILessonChapter;
}

const ChapterView = observer(({chapter}: ChapterViewProps) => {
  const theme = useTheme();

  return (
    <Frame
      appearance={'card'}
      direction="column"
      gap={3}
      padding={3}
      clip
      style={{backgroundColor: theme.palette.background.paper}}
    >
      <Typography
        variant="h4"
        fontWeight="bold"
        style={{
          margin: D.unit(-3),
          marginBottom: D.unit(),
          padding: '16px 24px',
          borderBottom: `1px solid ${theme.palette.divider}`,
        }}
      >
        {chapter.order! + 1}. {chapter.title}
      </Typography>

      {/* Content blocks */}
      {chapter.contentBlocks?.map((block) => (
        <ContentBlockView key={block.id} block={block}/>
      ))}
    </Frame>
  );
});

interface ContentBlockViewProps {
  block: lessons.IContentBlock;
}

const ContentBlockView = observer(({block}: ContentBlockViewProps) => {
  if (block.blockType === lessons.ContentBlockType.CONTENT_BLOCK_TEXT) {
    return <TextBlockView block={block}/>;
  } else if (block.blockType === lessons.ContentBlockType.CONTENT_BLOCK_ANNOTATION) {
    if (block.videoExample) {
      return <VideoExampleCard videoExample={block.videoExample}/>;
    }
    return null;
  }
  return null;
});

interface TextBlockViewProps {
  block: lessons.IContentBlock;
}

const TextBlockView = ({block}: TextBlockViewProps) => {
  const theme = useTheme();

  if (!block.richText || block.richText === "<p><br></p>") return null;

  const styled = !!textBlockStyleColor(block.textStyle, theme);

  return (
    <SanitizedHtml
      style={{
        ...textBlockStyleProps(block.textStyle, theme),
        ...(styled ? {padding: D.unit(2)} : {}),
      }}
      html={block.richText}
    />
  );
};
